import React from 'react';
import { OnlineUser, handleAvatarError } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { SelectableUser } from '../hooks/useAffiliateUserSearch';
import { Check } from 'lucide-react';
import { PresenceDot } from './PresenceDot';

interface UserPickerRowProps {
  user: SelectableUser;
  onlineUsers: OnlineUser[];
  onToggle: (userId: string | number) => void;
  /** 이름 오른쪽에 붙는 상태 배지 (관리자 콘솔의 승인 여부 등) */
  badge?: React.ReactNode;
  /**
   * 온라인 점 표시 여부. 접속 상태와 무관한 화면(관리자 콘솔 승인 명단)에서는
   * onlineUsers를 넘길 수 없어 전원이 회색 점으로 보이므로 끈다. 기본값은 표시.
   */
  showPresence?: boolean;
}

/**
 * 채팅방 개설(CreateGroupModal)·멤버 초대(InviteMemberModal) 공용 사용자 선택 행.
 *
 * 두 모달에 같은 마크업이 복제돼 있어 한쪽만 고치면 어긋났다. 한 화면에 더 많은 인원이
 * 보이도록 이름·부서를 세로 2줄이 아니라 가로 1줄로 붙이고 행 높이를 36px 내외로 낮춘다.
 * 이름은 shrink-0으로 온전히 보여주고, 남는 폭에서 부서명만 잘린다.
 */
export const UserPickerRow: React.FC<UserPickerRowProps> = ({
  user,
  onlineUsers,
  onToggle,
  badge,
  showPresence = true,
}) => {
  // 행마다 find를 네 번 돌리던 것을 한 번으로 (부서 전체가 렌더되면 인원수만큼 반복된다)
  const status = onlineUsers.find((u) => u.userId === String(user.id))?.status;

  return (
    <button
      onClick={() => onToggle(user.id)}
      className={`w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-slate-50 transition-colors ${
        user.selected ? 'bg-brand-100' : ''
      }`}
    >
      {/* 체크박스 */}
      <div
        className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
          user.selected ? 'bg-brand-600 border-brand-600' : 'border-slate-300'
        }`}
      >
        {user.selected && <Check size={10} className="text-white" />}
      </div>

      {/* 아바타 + 온라인 점 */}
      <div className="relative shrink-0">
        <img
          src={resolveAvatarUrl(user.avatar)} onError={handleAvatarError}
          alt=""
          loading="lazy"
          decoding="async"
          className="w-7 h-7 rounded-full object-cover bg-slate-100"
        />
        {showPresence && (
          <PresenceDot
            status={status ?? 'offline'}
            className="absolute -bottom-0.5 -right-0.5 w-2 h-2 border border-white"
          />
        )}
      </div>

      {/* 이름 · 부서 (가로 1줄) */}
      <div className="flex-1 min-w-0 flex items-baseline gap-2 text-left">
        <span className="text-sm text-slate-800 shrink-0">{user.username}</span>
        {user.depart && (
          <span className="text-xs text-slate-500 truncate min-w-0">{user.depart}</span>
        )}
      </div>

      {badge && <div className="shrink-0">{badge}</div>}
    </button>
  );
};
